import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { Banner } from '../types/banner';
import { ExternalLink } from 'lucide-react';

interface BannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  banner: Banner | null;
  onSave: (banner: Partial<Banner>) => Promise<void>;
  title: string;
}

export default function BannerModal({ isOpen, onClose, banner, onSave, title }: BannerModalProps) {
  const [formData, setFormData] = useState<Partial<Banner>>({
    image_url: '',
    title: '',
    description: '',
    link_url: '',
    is_active: true,
    display_order: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (banner) {
      setFormData({
        image_url: banner.image_url || '',
        title: banner.title || '',
        description: banner.description || '',
        link_url: banner.link_url || '',
        is_active: banner.is_active === 1 || banner.is_active === true,
        display_order: banner.display_order || 0
      });
      setPreviewImage(banner.image_url || null);
    } else {
      resetForm();
    }
  }, [banner]);

  const resetForm = () => {
    setFormData({
      image_url: '',
      title: '',
      description: '',
      link_url: '',
      is_active: true,
      display_order: 0
    });
    setPreviewImage(null);
    setValidationErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseInt(value) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.image_url?.trim()) {
      errors.image_url = 'Image URL is required';
    } else if (!isValidUrl(formData.image_url)) {
      errors.image_url = 'Please enter a valid URL';
    }
    
    if (!formData.title?.trim()) {
      errors.title = 'Title is required';
    }
    
    if (formData.link_url && !isValidUrl(formData.link_url)) {
      errors.link_url = 'Please enter a valid URL';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleImageUrlBlur = () => {
    if (formData.image_url && isValidUrl(formData.image_url)) {
      setPreviewImage(formData.image_url);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await onSave(formData);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error saving banner:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6 max-w-2xl w-full">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">{title}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4 md:col-span-1">
              <div>
                <label htmlFor="image_url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Image URL*
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="image_url"
                    id="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    onBlur={handleImageUrlBlur}
                    className={`w-full px-3 py-2 border ${
                      validationErrors.image_url ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
                    placeholder="https://example.com/image.jpg"
                  />
                  {validationErrors.image_url && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.image_url}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title*
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="title"
                    id="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${
                      validationErrors.title ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
                    placeholder="Banner Title"
                  />
                  {validationErrors.title && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.title}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <div className="mt-1">
                  <textarea
                    name="description"
                    id="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Banner Description"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="link_url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Link URL
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="link_url"
                    id="link_url"
                    value={formData.link_url}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${
                      validationErrors.link_url ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
                    placeholder="https://example.com/page"
                  />
                  {validationErrors.link_url && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.link_url}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Display Order
                  </label>
                  <div className="mt-1">
                    <input
                      type="number"
                      name="display_order"
                      id="display_order"
                      min="0"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                
                <div className="flex items-center h-full pt-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      id="is_active"
                      checked={formData.is_active === true || formData.is_active === 1}
                      onChange={(e) => handleInputChange({
                        ...e,
                        target: {
                          ...e.target,
                          name: 'is_active',
                          value: e.target.checked ? 'true' : 'false'
                        }
                      } as React.ChangeEvent<HTMLInputElement>)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Active
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="md:col-span-1">
              <div className="border border-gray-300 dark:border-gray-600 rounded-md p-4 h-full flex flex-col">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Image Preview</h3>
                <div className="flex-grow flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-md overflow-hidden">
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Banner Preview"
                      className="max-w-full max-h-60 object-contain"
                      onError={() => setPreviewImage(null)}
                    />
                  ) : (
                    <div className="text-gray-400 dark:text-gray-500 text-center p-4">
                      <p>No image preview available</p>
                      <p className="text-xs mt-1">Enter a valid image URL</p>
                    </div>
                  )}
                </div>
                {previewImage && (
                  <div className="mt-2 text-right">
                    <a
                      href={previewImage}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      Open in new tab <ExternalLink className="ml-1 h-3 w-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
